/**
 * Created by WorkAxis www.workaxis.in
 * WorkAxis — AI & product engineering partner.
 */
import React from 'react';
import "./style.css";
import {Link} from "react-router-dom";
import {
    serviceBuckets,
    idealCustomers,
    clientWork,
    clients,
    testimony,
    antcrmProofPoints
} from "./data";
import {CtaSection, LogoMarquee, SectionHead, WorkCarousel} from "./shared";
import {Parallax, Reveal, ScrollGrow, ScrollWords, TiltCard, Magnetic} from "./motion";
import Hero from "./Hero/Hero";
import WiloShowcase from "./WiloShowcase";

export default function Landing() {
    return (
        <div className="wx-landing">
            <div className="wx-ambient" aria-hidden="true"><span/><span/></div>

            <Parallax fade speed={80}>
                <Hero/>
            </Parallax>

            {/* ---------- Statement (words brighten with scroll) ---------- */}
            <section className="wx-section wx-statement-section">
                <div className="wx-container">
                    <ScrollWords className="wx-statement" parts={[
                        "WorkAxis is an AI and product engineering partner. We turn manual, fragmented work into",
                        <span className="wx-grad-text">production software</span>,
                        "— and we build and run our own products,",
                        <span className="wx-statement-chip">AntCRM</span>,
                        "and",
                        <span className="wx-statement-chip is-wilo">Wilo</span>,
                        "the same way."
                    ]}/>
                </div>
            </section>

            {/* ---------- Who we help ---------- */}
            <section className="wx-section wx-section-tight" id="who-we-help">
                <div className="wx-container">
                    <SectionHead
                        kicker="Who we help"
                        title="Built for teams that need to move faster without a large engineering org."
                    />
                    <div className="wx-services-grid is-three">
                        {idealCustomers.map((seg, i) => (
                            <TiltCard key={seg.name} className="wx-service-card" delay={i * 0.09}>
                                <div className="wx-service-title">{seg.name}</div>
                                <div className="wx-service-desc">{seg.message}</div>
                                <ul className="wx-product-points">
                                    {seg.signals.map((s) => <li key={s}>{s}</li>)}
                                </ul>
                            </TiltCard>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- Services ---------- */}
            <section className="wx-section" id="services">
                <div className="wx-container">
                    <SectionHead
                        kicker="Services"
                        title="Four ways we partner with you."
                        lead="If a process is manual, fragmented, or hard to scale, we map it, design the right
                            system, and build it into your stack — not another off-the-shelf tool you have to bend around."
                    />
                    <div className="wx-services-grid is-four-buckets">
                        {serviceBuckets.map((s, i) => (
                            <TiltCard key={s.name} className="wx-service-card" delay={(i % 2) * 0.09}>
                                <div className="wx-service-index">
                                    <span className="wx-index-line"/>{String(i + 1).padStart(2, '0')} /
                                </div>
                                <div className="wx-service-title">{s.name}</div>
                                <div className="wx-service-desc">{s.tagline}</div>
                                <p className="wx-bucket-desc">{s.desc}</p>
                            </TiltCard>
                        ))}
                    </div>
                    <Reveal className="wx-section-link">
                        <Link to="/services" className="wx-btn wx-btn-ghost">Explore how we work →</Link>
                    </Reveal>
                </div>
            </section>

            {/* ---------- Products ---------- */}
            <section className="wx-section" id="products">
                <div className="wx-container">
                    <SectionHead
                        kicker="Products & Work"
                        title="We build software like a product company — not a ticket factory."
                        lead="From idea to production — we design, build, and operate software that businesses
                            depend on. AntCRM and Wilo are two products we build and run ourselves."
                    />

                    <ScrollGrow className="wx-product-featured">
                        <div className="wx-product-featured-body">
                            <div className="wx-product-tag">Our product · Proof we ship</div>
                            <h3>AntCRM</h3>
                            <p>
                                Our customer relationship platform — designed, engineered, and operated entirely
                                in-house. We eat our own dog food: the same product discipline we bring to client work.
                            </p>
                            <ul className="wx-product-points">
                                {antcrmProofPoints.map((p) => <li key={p}>{p}</li>)}
                            </ul>
                            <Magnetic>
                                <a href="https://antcrm.co" target="_blank" rel="noreferrer"
                                   className="wx-btn wx-btn-primary">Explore AntCRM</a>
                            </Magnetic>
                        </div>
                        <div className="wx-product-featured-visual">
                            <Parallax speed={28}>
                                <div className="wx-ant-mark">
                                    <div className="wx-ant-logo wx-grad-text">AntCRM</div>
                                    <div className="wx-ant-domain">antcrm.co</div>
                                </div>
                            </Parallax>
                        </div>
                    </ScrollGrow>

                    <WiloShowcase/>

                </div>
                <WorkCarousel items={clientWork}/>
                <div className="wx-container">
                    <Reveal className="wx-section-link">
                        <Link to="/products" className="wx-btn wx-btn-ghost">View case studies →</Link>
                    </Reveal>
                </div>
            </section>

            {/* ---------- Clients ---------- */}
            <section className="wx-section wx-section-tight">
                <div className="wx-container">
                    <SectionHead kicker="Trusted by" center/>
                </div>
                <LogoMarquee logos={clients}/>
            </section>

            {/* ---------- Testimonials ---------- */}
            <section className="wx-section wx-section-tight">
                <div className="wx-container">
                    <SectionHead kicker="Testimonials" title="What partners say." center/>
                </div>
                <div className="wx-marquee">
                    {[0, 1].map((dup) => (
                        <div className="wx-marquee-track" aria-hidden={dup === 1} key={dup}>
                            {testimony.map((t) => (
                                <div className="wx-quote-card" key={dup + t.name}>
                                    <div className="wx-quote-text">{t.command}</div>
                                    <div className="wx-quote-person">
                                        <div className="wx-quote-name">{t.name}</div>
                                        <div className="wx-quote-role">{t.designation}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            <CtaSection/>
        </div>
    );
}
